function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

let subscribers = {};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function publish(eventName, data) {
  if (subscribers[eventName]) {
    subscribers[eventName].forEach((callback) => {
      callback(data);
    });
  }
}

const PUB_SUB_EVENTS = {
  cartAdd: "cart-add",
  cartUpdate: "cart-update",
};

//Drawer used for mobile navigation and mini cart
class SideDrawerToggle extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", this.handleToggle.bind(this));

    this.addEventListener("keydown", (evt) => {
      if (evt.code === "Enter" || evt.code === "Space") {
        evt.preventDefault();
        this.handleToggle(evt);
      }
    });
  }

  handleToggle(evt) {
    // Close all drawers
    if (this.getAttribute("data-drawer") == "all") {
      const activeDrawers = document.querySelectorAll("side-drawer.active");
      if (activeDrawers) {
        activeDrawers.forEach((el) => {
          const event = new Event("drawer-toggle");
          el.dispatchEvent(event);
        });
      }
    } else {
      const drawer = document.getElementById(this.getAttribute("data-drawer"));
      if (drawer) {
        const event = new Event("drawer-toggle");
        drawer.dispatchEvent(event);
      }
    }
  }
}

customElements.define("side-drawer-toggle", SideDrawerToggle);

class SideDrawer extends HTMLElement {
  constructor() {
    super();
    this._wasOpen = false;

    const thisElement = this;

    this.observer = new MutationObserver(() => {
      if (this._wasOpen && !this.hasAttribute("drawer-open")) {
        thisElement.open(this);
      }
    });

    this.observer.observe(this, {
      attributes: true,
      attributeFilter: ["drawer-open"],
    });

    //Toggle event listeners
    this.addEventListener("drawer-toggle", function () {
      if (this.hasAttribute("drawer-open")) {
        thisElement.close(this);
      } else {
        thisElement.open(this);
      }
    });

    //Key up and body click events
    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close(),
    );
    this.onBodyClick = this.handleBodyClick.bind(this);
  }

  //Open drawer
  open(drawer) {
    this._wasOpen = true;
    this.setAttribute("drawer-open", "");
    this.classList.add("active");
    document.body.classList.add("blur");
    document.body.classList.add("drawer--active");
    this.addEventListener(
      "transitionend",
      () => {
        drawer.focus();
        trapFocus(drawer);
      },
      { once: true },
    );

    document.querySelector(".header").classList.remove("header--search-active");

    document.body.addEventListener("click", this.onBodyClick, {
      passive: true,
    });
  }

  //Close drawer
  close(drawer) {
    this._wasOpen = false;
    this.removeAttribute("drawer-open");
    this.classList.remove("active");
    const activeDrawers = document.querySelectorAll(".drawer.active");
    if (activeDrawers.length == 0) {
      if (
        !document
          .querySelector(".header")
          .classList.contains("header--search-active")
      ) {
        document.body.classList.remove("blur");
      }

      document.body.classList.remove("drawer--active");
    }

    document.body.removeEventListener("click", this.onBodyClick, {
      passive: true,
    });
    removeTrapFocus(this);

    if (this.id === "mini-cart") {
      const cartTitle = this.querySelector(".cart-drawer__title--default");
      const cartAdded = this.querySelector(".cart-drawer__title--added");
      if (cartTitle && cartAdded) {
        setTimeout(() => {
          cartTitle.classList.remove("hidden");
          cartAdded.classList.add("hidden");
        }, 350);
      }
      delete document.body.dataset.cartAction;
    }

    if (this.id === "mobile-menu") {
      this.closeOtherDrawers();
    }
  }

  closeOtherDrawers() {
    document.querySelectorAll("side-drawer").forEach((drawer) => {
      if (drawer !== this && drawer.hasAttribute("drawer-open")) {
        drawer.close();
      }
    });
  }

  //Body click event to close drawer
  handleBodyClick(evt) {
    const target = evt.target;

    if (!target || !target.isConnected) return;

    const drawerElement = target.closest("side-drawer");
    const isInsideAnyDrawer = drawerElement !== null;
    const isOnToggle = target.closest("side-drawer-toggle") !== null;

    if (!isInsideAnyDrawer && !isOnToggle) {
      this.close();
    }
  }
}

customElements.define("side-drawer", SideDrawer);

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  );
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.cartItemsComponent = this.closest("cart-items");
    this.input = this.querySelector("input");
    this.key = this.input.getAttribute("data-item-key");
    this.changeEvent = new Event("change", { bubbles: true });
    this.minusButton = this.querySelector('button[name="minus"]');
    this.plusButton = this.querySelector('button[name="plus"]');

    this.querySelectorAll("button").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        this.onButtonClick(button);
      });

      if (this.input.value == this.input.min) {
        this.minusButton.setAttribute("disabled", true);
      }
    });

    this.input.addEventListener("input", () => {
      if (this.input.value === "" || this.input.value === "0") return;
    });

    this.input.addEventListener("blur", () => {
      this.handleManualUpdate();
    });

    this.input.addEventListener("keyup", (event) => {
      if (event.key === "Enter") {
        this.input.blur();
      }
    });
  }

  onButtonClick(button) {
    const previousValue = this.input.value;

    button.name === "plus" ? this.input.stepUp() : this.input.stepDown();

    if (previousValue !== this.input.value) {
      if (this.cartItemsComponent) {
        this.cartItemsComponent.classList.add("loading");
      }
      this.updateQuantity();
    }

    if (this.input.value == this.input.min) {
      this.minusButton.setAttribute("disabled", true);
    } else {
      this.minusButton.removeAttribute("disabled");
    }

    if (
      this.input.max &&
      parseInt(this.input.value, 10) >= parseInt(this.input.max, 10)
    ) {
      this.plusButton.setAttribute("disabled", true);
    } else {
      this.plusButton.removeAttribute("disabled");
    }
  }

  updateQuantity() {
    let value = parseInt(this.input.value, 10);

    if (isNaN(value) || value < 1) {
      value = 1;
      this.input.value = 1;
    }

    const maximum = parseInt(this.input.max, 10);
    if (!isNaN(maximum) && value > maximum) {
      value = maximum;
      this.input.value = maximum;
    }

    updateCartItem(this.key, value);
    this.input.dispatchEvent(this.changeEvent);
  }

  handleManualUpdate() {
    let value = parseInt(this.input.value, 10);

    if (isNaN(value) || value < 1) {
      this.input.value = this.input.defaultValue;
      return;
    }

    if (this.cartItemsComponent) {
      this.cartItemsComponent.classList.add("loading");
    }

    this.updateQuantity();
  }
}

customElements.define("quantity-input", QuantityInput);

class AccordionCustom extends HTMLElement {
  constructor() {
    super();
    this.details = this.querySelector("details");
    this.summary = this.querySelector("summary");
    this.controller = new AbortController();
  }

  connectedCallback() {
    if (!this.details || !this.summary) return;

    this.setDefaultOpenState();
    this.addEventListeners();
  }

  disconnectedCallback() {
    this.controller.abort();
  }

  addEventListeners() {
    const { signal } = this.controller;

    this.addEventListener("keydown", (e) => this.handleKeyDown(e), { signal });
    this.summary.addEventListener("click", (e) => this.handleClick(e), {
      signal,
    });
    window
      .matchMedia("(min-width: 990px)")
      .addEventListener("change", () => this.setDefaultOpenState(), { signal });
  }

  handleClick(event) {
    const isMobile = window.innerWidth < 750;
    const isDesktop = !isMobile;

    if (
      (isMobile && this.dataset.disableOnMobile === "true") ||
      (isDesktop && this.dataset.disableOnDesktop === "true")
    ) {
      event.preventDefault();
    }
  }

  handleKeyDown(event) {
    if (event.key === "Escape" && this.dataset.closeWithEscape === "true") {
      event.preventDefault();
      this.details.open = false;
      this.summary.focus();
      this.controller = new AbortController();
    }
  }

  setDefaultOpenState() {
    if (this.details.hasAttribute("open")) return;

    const isMobile = window.innerWidth < 750;

    this.details.open =
      (isMobile && this.hasAttribute("open-by-default-on-mobile")) ||
      (!isMobile && this.hasAttribute("open-by-default-on-desktop"));
  }
}

if (!customElements.get("accordion-custom")) {
  customElements.define("accordion-custom", AccordionCustom);
}

class VariantSelects extends HTMLElement {
  constructor() {
    super();

    document
      .querySelectorAll(
        '.product-form__input input[type="radio"], .product-form__input select',
      )
      .forEach((option) =>
        option.addEventListener("change", this.whichInputUpdated.bind(option)),
      );

    this.addEventListener("change", this.onVariantChange);
  }

  connectedCallback() {
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.updateOptions();
      this.updateVariantStatuses();
    });
  }

  whichInputUpdated(element) {
    var optionname = element.target
      .closest(".product-form__input")
      .querySelector(".form__label").dataset.optionname;

    document
      .querySelectorAll(`.product-form__input`)
      .forEach((option) => option.classList.remove("activeset"));

    document
      .querySelectorAll(`.form__label[data-optionname="${optionname}"]`)
      .forEach((option) =>
        option.closest(".product-form__input").classList.add("activeset"),
      );
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.removeErrorMessage();
    this.updateVariantStatuses();

    if (!this.currentVariant) {
      this.setUnavailable();
      return;
    }

    this.updateMedia();
    this.updateURL();
    this.updateVariantInput();
    this.updateSelectedValues();
    this.renderProductInfo();

    //update stackline sku if present
    if (
      window?.bcProductMetaData &&
      window.bcProductMetaData.variations &&
      window.bcProductMetaData.variations[this.currentVariant.id]
    ) {
      window.bcProductMetaData.stacklineSku =
        window.bcProductMetaData.variations[
          this.currentVariant.id
        ].stacklineSku;
    } else if (window?.bcProductMetaData) {
      window.bcProductMetaData.stacklineSku = null;
    }
  }

  updateOptions() {
    this.options = Array.from(
      this.querySelectorAll("select"),
      (select) => select.value,
    );
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  updateMedia() {
    const mediaGalleries = document.querySelectorAll(
      `[id^="MediaGallery-${this.dataset.section}"]`,
    );

    mediaGalleries.forEach((mediaGallery) => {
      if (this.currentVariant?.featured_media) {
        mediaGallery.setActiveMedia(
          `${this.currentVariant.featured_media.id}`,
          true,
        );
      } else {
        mediaGallery.setActiveVariant(this.currentVariant?.id);
      }
    });
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === "false") return;
    window.history.replaceState(
      {},
      "",
      `${this.dataset.url}?variant=${this.currentVariant.id}`,
    );
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  updateSelectedValues() {
    if (!this.currentVariant) return;

    this.querySelectorAll(".form__label[data-optionname]").forEach(
      (legend, index) => {
        const selectedValueEl = legend.querySelector(".selected-value");
        if (
          selectedValueEl &&
          this.currentVariant.options[index] !== undefined
        ) {
          selectedValueEl.textContent = this.currentVariant.options[index];
        }
      },
    );
  }

  updateVariantStatuses() {
    const variants = this.getVariantData();
    const inputWrappers = [...this.querySelectorAll(".product-form__input")];

    // Current selected value per option position (option1, option2, ...)
    const selectedValues = inputWrappers.map((wrapper) => {
      const selected = wrapper.querySelector(
        'input[type="radio"]:checked, option:checked',
      );
      return selected?.value || null;
    });

    inputWrappers.forEach((optionWrapper, optionIndex) => {
      const optionInputs = [
        ...optionWrapper.querySelectorAll('input[type="radio"], option'),
      ];

      const matchingVariants = variants.filter((variant) =>
        selectedValues.every((selectedValue, selectedIndex) => {
          // Ignore current option; evaluate compatibility against all others
          if (selectedIndex === optionIndex) return true;
          if (!selectedValue) return true;
          return variant[`option${selectedIndex + 1}`] === selectedValue;
        }),
      );

      const optionStatuses = new Map();
      optionInputs.forEach((input) => {
        const value = input.getAttribute("value");
        const valueVariants = matchingVariants.filter(
          (variant) => variant[`option${optionIndex + 1}`] === value,
        );

        let status = "unavailable";
        if (valueVariants.some((variant) => this.variantCanSell(variant))) {
          status = "available";
        } else if (
          valueVariants.some(
            (variant) => this.getVariantStatus(variant) === "sold-out",
          )
        ) {
          status = "sold-out";
        }

        optionStatuses.set(value, status);
      });

      this.setInputAvailability(optionInputs, optionStatuses);
    });
  }

  setInputAvailability(listOfOptions, optionStatuses) {
    listOfOptions.forEach((input) => {
      const value = input.getAttribute("value");
      const status = optionStatuses.get(value);

      if (status === "unavailable") {
        input.innerText = window.variantStrings.unavailable_with_option.replace(
          "[value]",
          value,
        );
      } else if (status === "sold-out") {
        input.innerText = `${value} - ${window.variantStrings.soldOut}`;
      } else {
        input.innerText = value;
      }
    });
  }

  removeErrorMessage() {
    const section = this.closest("section");
    if (!section) return;

    const productForm = section.querySelector("product-form");
    if (productForm) productForm.handleErrorMessage();
  }

  renderProductInfo() {
    const requestedVariantId = this.currentVariant.id;
    const sectionId = this.dataset.originalSection
      ? this.dataset.originalSection
      : this.dataset.section;

    const stickyHeaderEl = document.querySelector(
      "pdp-sticky-header[data-section-id]",
    );
    const stickySectionId = stickyHeaderEl
      ? stickyHeaderEl.dataset.sectionId
      : null;

    const url = `${this.dataset.url}?variant=${requestedVariantId}${
      stickySectionId
        ? `&sections=${sectionId},${stickySectionId}`
        : `&section_id=${sectionId}`
    }`;

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        if (this.currentVariant.id !== requestedVariantId) return;

        let html;
        try {
          const json = JSON.parse(responseText);
          html = json;
        } catch {
          html = {
            [sectionId]: responseText,
          };
        }

        if (html[sectionId]) {
          const doc = new DOMParser().parseFromString(
            html[sectionId],
            "text/html",
          );

          const newAddButton = doc.getElementById(
            `ProductSubmitButton-${sectionId}`,
          );
          const currentAddButton = document.getElementById(
            `ProductSubmitButton-${sectionId}`,
          );

          if (newAddButton && currentAddButton) {
            currentAddButton.innerHTML = newAddButton.innerHTML;

            if (newAddButton.hasAttribute("disabled")) {
              currentAddButton.setAttribute("disabled", "disabled");
              currentAddButton.setAttribute("aria-disabled", "true");
            } else {
              currentAddButton.removeAttribute("disabled");
              currentAddButton.removeAttribute("aria-disabled");
            }

            if (newAddButton.dataset.maximumQuantityDisabled === "true") {
              currentAddButton.dataset.maximumQuantityDisabled = "true";
            } else {
              delete currentAddButton.dataset.maximumQuantityDisabled;
            }
            if (newAddButton.dataset.baseDisabled === "true") {
              currentAddButton.dataset.baseDisabled = "true";
            } else {
              delete currentAddButton.dataset.baseDisabled;
            }

            const newProductForm = newAddButton.closest("product-form");
            const currentProductForm = currentAddButton.closest("product-form");
            currentProductForm.dataset.variantId =
              newProductForm.dataset.variantId;
            if (newProductForm.dataset.maximumOrderQuantity) {
              currentProductForm.dataset.maximumOrderQuantity =
                newProductForm.dataset.maximumOrderQuantity;
            } else {
              delete currentProductForm.dataset.maximumOrderQuantity;
            }

            const newMaximumMessage = newProductForm.querySelector(
              ".product-form__maximum-order-message",
            );
            const currentMaximumMessage = currentProductForm.querySelector(
              ".product-form__maximum-order-message",
            );
            if (newMaximumMessage && currentMaximumMessage) {
              currentMaximumMessage.innerHTML = newMaximumMessage.innerHTML;
              currentMaximumMessage.hidden = newMaximumMessage.hidden;
            }
          }

          const destinationPrice = document.getElementById(
            `price-${this.dataset.section}`,
          );
          const sourcePrice = doc.getElementById(`price-${sectionId}`);

          if (sourcePrice && destinationPrice) {
            destinationPrice.innerHTML = sourcePrice.innerHTML;
            destinationPrice.classList.remove("hidden");
          } else {
            destinationPrice.classList.add("hidden");
          }

          const destinationSku = document.querySelector(`.product-sku`);
          const sourceSku = doc.querySelector(`.product-sku`);

          if (sourceSku && destinationSku) {
            destinationSku.innerHTML = sourceSku.innerHTML;
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId,
              html: doc,
              variant: this.currentVariant,
            },
          });
        }

        if (stickySectionId && html[stickySectionId]) {
          const docSticky = new DOMParser().parseFromString(
            html[stickySectionId],
            "text/html",
          );

          const newStickyButton = docSticky.querySelector(
            ".product-card__form",
          );
          const currentStickyButton = document.querySelector(
            "pdp-sticky-header .product-card__form",
          );

          if (newStickyButton && currentStickyButton) {
            currentStickyButton.innerHTML = newStickyButton.innerHTML;
          }
        }
      });
  }

  setUnavailable() {
    const productForm = document.getElementById(
      `product-form-${this.dataset.section}`,
    );
    const addButton = productForm?.querySelector('[name="add"]');
    if (addButton) {
      addButton.setAttribute("disabled", "disabled");
      addButton.textContent = window.variantStrings.unavailable;
    }

    const price = document.getElementById(`price-${this.dataset.section}`);
    if (price) price.classList.add("hidden");

    this.querySelectorAll(".selected-value").forEach((el) => {
      el.textContent = "—";
    });
  }

  getVariantData() {
    this.variantData =
      this.variantData ||
      JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }

  getInventoryPolicyMap() {
    this.inventoryPolicyMap =
      this.inventoryPolicyMap ||
      (() => {
        const inventoryPolicyEl = this.querySelector(
          '[type="application/json"][data-inventory-policy]',
        );

        if (!inventoryPolicyEl) return new Map();

        try {
          const policies = JSON.parse(inventoryPolicyEl.textContent);
          return new Map(
            policies.map((policy) => [
              Number(policy.id),
              policy.inventory_policy,
            ]),
          );
        } catch {
          return new Map();
        }
      })();

    return this.inventoryPolicyMap;
  }

  getVariantInventoryPolicy(variant) {
    if (typeof variant.inventory_policy === "string") {
      return variant.inventory_policy;
    }

    return this.getInventoryPolicyMap().get(Number(variant.id));
  }

  getVariantStatus(variant) {
    this.variantStatusMap =
      this.variantStatusMap ||
      (() => {
        const statusEl = this.querySelector(
          '[type="application/json"][data-variant-status]',
        );

        if (!statusEl) return new Map();

        try {
          const statuses = JSON.parse(statusEl.textContent);
          return new Map(
            statuses.map((status) => [Number(status.id), status.status]),
          );
        } catch {
          return new Map();
        }
      })();

    return this.variantStatusMap.get(Number(variant.id));
  }

  variantCanSell(variant) {
    const explicitStatus = this.getVariantStatus(variant);
    if (explicitStatus) return explicitStatus === "available";

    // Stackline markets sell via retailers, so Shopify inventory never gates option selection there.
    if (this.dataset.stacklineActive === "true") return true;

    return (
      Boolean(variant.available) ||
      this.getVariantInventoryPolicy(variant) === "continue"
    );
  }
}

customElements.define("variant-selects", VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  setInputAvailability(listOfOptions, optionStatuses) {
    listOfOptions.forEach((input) => {
      const status = optionStatuses.get(input.getAttribute("value"));

      if (status === "available") {
        input.classList.remove("disabled");
        input.classList.remove("unavailable");
      } else {
        input.classList.add("disabled");
        input.classList.toggle("unavailable", status === "unavailable");
      }

      const label = this.querySelector(`label[for="${input.id}"]`);
      label?.querySelectorAll("[data-option-status]").forEach((message) => {
        message.hidden = message.dataset.optionStatus !== status;
      });
    });
  }

  updateOptions() {
    const inputFields = Array.from(
      this.querySelectorAll(".product-form__input"),
    );

    this.options = inputFields.map((fields) => {
      return Array.from(fields.querySelectorAll("input")).find(
        (radio) => radio.checked,
      ).value;
    });
  }
}

customElements.define("variant-radios", VariantRadios);

class ProductGallery extends HTMLElement {
  constructor() {
    super();
    this.splide = null;
    this.mediaIdToIndex = new Map();
    this.variantIdToIndex = new Map();
  }

  connectedCallback() {
    const splideEl = this.querySelector(".splide");
    if (!splideEl) return;

    const slides = splideEl.querySelectorAll(".splide__slide");
    slides.forEach((slide, index) => {
      const mediaId = slide.dataset.mediaId;
      if (mediaId) this.mediaIdToIndex.set(mediaId, index);

      const variantIds = slide.dataset.variantIds;
      if (variantIds) {
        variantIds
          .split(",")
          .filter(Boolean)
          .forEach((id) => {
            this.variantIdToIndex.set(id, index);
          });
      }
    });

    const slidesCount = slides.length;

    const splideOptions = {
      type: slidesCount > 1 ? "loop" : "slide",
      perPage: 1,
      arrows: true,
      pagination: true,
      drag: slidesCount > 1,
      speed: 600,
      video: {
        loop: true,
        mute: true,
      },
    };

    if (this.dataset.mode === "card") {
      splideOptions.lazyLoad = "nearby";
      splideOptions.preloadPages = 0;
    }

    this.splide = new Splide(splideEl, splideOptions);

    this.splide.mount();

    const navSelectors = [".splide__arrows", ".splide__pagination"];

    this.splide.on("move", () => {
      const active = document.activeElement;
      if (active && active.closest(".splide__slide")) {
        active.blur();
      }
    });

    navSelectors.forEach((selector) => {
      const nav = splideEl.querySelector(selector);
      if (!nav) return;

      nav.addEventListener("pointerdown", (e) => {
        e.preventDefault();
        e.stopPropagation();
      });

      nav.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
      });
    });

    this.onLayoutUpdated = () => {
      if (document.activeElement?.closest("product-gallery")) {
        document.activeElement.blur();
      }

      requestAnimationFrame(() => {
        this.rebuild();
      });
    };

    document.addEventListener("layout:updated", this.onLayoutUpdated);
  }

  rebuild() {
    if (this.splide) {
      this.splide.destroy(true);
      this.splide = null;
    }
    this.mediaIdToIndex.clear();
    this.variantIdToIndex.clear();
    this.connectedCallback();
  }

  disconnectedCallback() {
    document.removeEventListener("layout:updated", this.onLayoutUpdated);

    if (this.splide) {
      this.splide.destroy();
    }
  }

  setActiveMedia(mediaId) {
    if (!this.splide || !this.mediaIdToIndex.has(mediaId)) return;

    const logicalIndex = this.mediaIdToIndex.get(mediaId);
    this.splide.go(logicalIndex);
  }

  setActiveVariant(variantId) {
    if (!this.splide || !this.variantIdToIndex.has(String(variantId)))
      return false;
    this.splide.go(this.variantIdToIndex.get(String(variantId)));
    return true;
  }
}

customElements.define("product-gallery", ProductGallery);

class VideoBlock extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector("video") || this.querySelector("iframe");
    if (!this.video) return;

    this.isIframe = this.video.tagName === "IFRAME";
    this.isYoutube = this.isIframe && this.video.src.includes("youtube");
    this.isVimeo = this.isIframe && this.video.src.includes("vimeo");

    this.playButton = this.querySelector(".video-play-button");
    this.muteButton = this.querySelector(".video-mute-button");

    this.playing = this.dataset.autoplay === "1";
    this.muted = this.dataset.muted === "1";

    this.setupEvents();
  }

  setupEvents() {
    if (this.playButton) {
      this.playButton.addEventListener("click", (e) => {
        e.preventDefault();
        this.togglePlay();
      });
    }

    if (this.muteButton) {
      this.muteButton.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleMute();
      });
    }

    // Only HTML video has events we can listen to
    if (!this.isIframe) {
      ["play", "pause", "ended"].forEach((evt) => {
        this.video.addEventListener(evt, () => {
          this.playing = !this.video.paused;
          this.syncPlayIcon();
        });
      });
    }
  }

  togglePlay() {
    if (!this.isIframe) {
      if (this.video.paused) {
        this.video.play();
        this.playing = true;
      } else {
        this.video.pause();
        this.playing = false;
      }
    }

    if (this.isYoutube) {
      this.video.contentWindow.postMessage(
        JSON.stringify({
          event: "command",
          func: this.playing ? "pauseVideo" : "playVideo",
        }),
        "*",
      );
      this.playing = !this.playing;
    }

    if (this.isVimeo) {
      this.video.contentWindow.postMessage(
        {
          method: this.playing ? "pause" : "play",
        },
        "*",
      );
      this.playing = !this.playing;
    }

    this.syncPlayIcon();
  }

  toggleMute() {
    if (!this.isIframe) {
      this.video.muted = !this.video.muted;
      this.muted = this.video.muted;
    }

    if (this.isYoutube) {
      this.video.contentWindow.postMessage(
        JSON.stringify({
          event: "command",
          func: this.muted ? "unMute" : "mute",
        }),
        "*",
      );
      this.muted = !this.muted;
    }

    if (this.isVimeo) {
      this.video.contentWindow.postMessage(
        {
          method: "setVolume",
          value: this.muted ? 1 : 0,
        },
        "*",
      );
      this.muted = !this.muted;
    }

    this.syncMuteIcon();
  }

  syncPlayIcon() {
    this.playButton?.setAttribute(
      "aria-label",
      this.playing ? "Pause video" : "Play video",
    );

    this.playButton
      ?.querySelector(".video-play")
      ?.classList.toggle("hidden", this.playing);

    this.playButton
      ?.querySelector(".video-pause")
      ?.classList.toggle("hidden", !this.playing);
  }

  syncMuteIcon() {
    this.muteButton?.setAttribute(
      "aria-label",
      this.muted ? "Unmute video" : "Mute video",
    );

    this.muteButton
      ?.querySelector(".video-volume-on")
      ?.classList.toggle("hidden", this.muted);

    this.muteButton
      ?.querySelector(".video-volume-off")
      ?.classList.toggle("hidden", !this.muted);
  }
}

customElements.define("video-block", VideoBlock);

document.addEventListener("click", function (e) {
  const anchorLink = e.target.closest("[data-scroll-to]");
  if (!anchorLink) return;

  e.preventDefault();

  const targetId = anchorLink.getAttribute("data-scroll-to");
  const targetElement = document.querySelector(targetId);
  if (!targetElement) return;

  const stickyHeader = document.querySelector("pdp-sticky-header");
  if (stickyHeader && typeof stickyHeader.closeAnchors === "function") {
    stickyHeader.closeAnchors();
  }

  let offset = 0;

  const mainHeader = document.querySelector(".header");
  if (mainHeader) {
    offset += mainHeader.offsetHeight;
  }

  if (stickyHeader && stickyHeader.classList.contains("is-sticky")) {
    offset += stickyHeader.offsetHeight;
  }

  const y = targetElement.getBoundingClientRect().top + window.scrollY - offset;

  window.scrollTo({
    top: y,
    behavior: "smooth",
  });
});

/* Tabbed Content */
class TabbedContent extends HTMLElement {
  connectedCallback() {
    this.tab_buttons = this.querySelectorAll(".js-tab-button");
    this.tab_panels = this.querySelectorAll(".js-tab-panel");

    this.tab_buttons.forEach((button) => {
      button.addEventListener("click", (event) => {
        if (!event.isTrusted) return;

        event.preventDefault();
        this.activateTab(button);
      });

      button.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          this.activateTab(button);
        }
      });
    });
  }

  activateTab(button) {
    const targetPanelId = button.getAttribute("aria-controls");
    const targetPanel = targetPanelId
      ? this.querySelector(`#${CSS.escape(targetPanelId)}`)
      : null;

    // Don't blank the UI if the target panel can't be found
    if (!targetPanel) return;

    // Deactivate all buttons
    this.tab_buttons.forEach((btn) => {
      btn.setAttribute("aria-selected", "false");
      btn.classList.remove("active");
      btn.classList.add("button--outline");
    });

    // Hide all panels + close accordions
    this.tab_panels.forEach((panel) => {
      panel.classList.add("visually-hidden");

      const accordionMenu = panel.querySelector("accordion-menu");
      if (accordionMenu && typeof accordionMenu.closeAll === "function") {
        accordionMenu.closeAll();
      }
    });

    // Activate selected button/panel
    button.setAttribute("aria-selected", "true");
    button.classList.add("active");
    button.classList.remove("button--outline");
    targetPanel.classList.remove("visually-hidden");
  }
}

customElements.define("tabbed-content", TabbedContent);

window.customElements.define(
  "layout-toggle",
  class GridToggle extends HTMLElement {
    constructor() {
      super();
      this.largeGridViewButton = this.querySelector("#largeGridViewButton");
      this.mediumGridViewButton = this.querySelector("#mediumGridViewButton");
      this.smallGridViewButton = this.querySelector("#smallGridViewButton");
      this.loadedProducts = false;

      this.largeGridViewButton?.addEventListener(
        "click",
        this.onButtonClick.bind(this),
      );
      this.mediumGridViewButton?.addEventListener(
        "click",
        this.onButtonClick.bind(this),
      );
      this.smallGridViewButton?.addEventListener(
        "click",
        this.onButtonClick.bind(this),
      );

      const gridView = localStorage.getItem("layout-grid-option");

      if (gridView == "grid--large") {
        this.largeGridViewButton.click();
      } else if (gridView == "grid--medium") {
        this.mediumGridViewButton.click();
      } else if (gridView == "grid--small") {
        this.smallGridViewButton.click();
      }
    }

    onButtonClick(event) {
      event.preventDefault();
      if (document.activeElement) {
        document.activeElement.blur();
      }

      const colID = event.currentTarget.id;
      if (colID == "largeGridViewButton") {
        const largeGridViewButton = document.querySelectorAll(
          "#largeGridViewButton",
        );
        const mediumGridViewButton = document.querySelectorAll(
          "#mediumGridViewButton",
        );
        const smallGridViewButton = document.querySelectorAll(
          "#smallGridViewButton",
        );

        largeGridViewButton.forEach((element) => {
          document.getElementById("product-grid").classList.add("grid--large");
          document
            .getElementById("product-grid")
            .classList.remove("grid--medium");
          document
            .getElementById("product-grid")
            .classList.remove("grid--small");
          element.classList.add("active");
        });

        mediumGridViewButton.forEach((element) => {
          element.classList.remove("active");
        });

        smallGridViewButton.forEach((element) => {
          element.classList.remove("active");
        });

        localStorage.setItem("layout-grid-option", "grid--large");
      } else if (colID == "mediumGridViewButton") {
        const largeGridViewButton = document.querySelectorAll(
          "#largeGridViewButton",
        );
        const mediumGridViewButton = document.querySelectorAll(
          "#mediumGridViewButton",
        );
        const smallGridViewButton = document.querySelectorAll(
          "#smallGridViewButton",
        );

        mediumGridViewButton.forEach((element) => {
          document
            .getElementById("product-grid")
            .classList.remove("grid--large");
          document.getElementById("product-grid").classList.add("grid--medium");
          document
            .getElementById("product-grid")
            .classList.remove("grid--small");

          element.classList.add("active");
        });

        largeGridViewButton.forEach((element) => {
          element.classList.remove("active");
        });

        smallGridViewButton.forEach((element) => {
          element.classList.remove("active");
        });

        localStorage.setItem("layout-grid-option", "grid--medium");
      } else if (colID == "smallGridViewButton") {
        const largeGridViewButton = document.querySelectorAll(
          "#largeGridViewButton",
        );
        const mediumGridViewButton = document.querySelectorAll(
          "#mediumGridViewButton",
        );
        const smallGridViewButton = document.querySelectorAll(
          "#smallGridViewButton",
        );

        smallGridViewButton.forEach((element) => {
          document
            .getElementById("product-grid")
            .classList.remove("grid--large");
          document
            .getElementById("product-grid")
            .classList.remove("grid--medium");
          document.getElementById("product-grid").classList.add("grid--small");

          element.classList.add("active");
        });

        largeGridViewButton.forEach((element) => {
          element.classList.remove("active");
        });

        mediumGridViewButton.forEach((element) => {
          element.classList.remove("active");
        });

        localStorage.setItem("layout-grid-option", "grid--small");
      }

      const productGrid = document.getElementById("product-grid");
      if (!productGrid) return;

      // The default small view is two columns on mobile. When a customer
      // switches to a wider card layout, update the responsive-image hint so
      // the browser can request a sharper source only when it is needed.
      const mobileSizes = productGrid.classList.contains("grid--small")
        ? "50vw"
        : "100vw";

      productGrid
        .querySelectorAll('source[media="(max-width: 749px)"]')
        .forEach((source) => source.setAttribute("sizes", mobileSizes));

      document.dispatchEvent(new CustomEvent("layout:updated"));
    }
  },
);

class ImageGallery extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.nav = this.querySelector(".image-gallery__tab-navigation");
    this.tabs = [...this.querySelectorAll("image-gallery-tab")];
    this.activeTabId = null;
    this.splides = new Map();

    if (this.tabs.length <= 1) {
      this.nav.style.display = "none";
      this.openFirstTab();
      return;
    }

    this.setupTabsNavigation();
    this.openFirstTab();
  }

  setupTabsNavigation() {
    this.tabs.forEach((tab) => {
      const btn = tab.querySelector(".image-gallery__tab-btn");
      const tabId = tab.dataset.tabId;

      btn.dataset.tabId = tabId;
      this.nav.appendChild(btn);

      btn.addEventListener("click", () => this.onTabClick(tabId));
    });
  }

  openFirstTab() {
    const first = this.tabs[0];
    if (!first) return;
    this.onTabClick(first.dataset.tabId);
  }

  onTabClick(tabId) {
    if (this.activeTabId === tabId) return;
    this.activeTabId = tabId;

    this.nav.querySelectorAll("button").forEach((btn) => {
      btn.classList.toggle("active", btn.dataset.tabId === tabId);
    });

    this.tabs.forEach((tab) => {
      tab.style.display = tab.dataset.tabId === tabId ? "block" : "none";
    });

    this.initSplide(tabId);
  }

  initSplide(tabId) {
    const tab = this.tabs.find((t) => t.dataset.tabId === tabId);
    if (!tab) return;

    const splideEl = tab.querySelector(".splide");

    if (this.splides.has(tabId)) return;

    const splide = new Splide(splideEl, {
      type: "slide",
      perPage: 1,
      arrows: true,
      pagination: false,
    });

    const navigation = splide.root.querySelector(".carousel-navigation");
    const bar = navigation.querySelector(".carousel-navigation-progress-bar");

    const updateProgress = () => {
      if (!bar) return;

      const total = splide.length;
      const perPage = splide.options.perPage;
      const currentIndex = splide.index;

      const covered = Math.min(currentIndex + perPage, total);
      const progress = total === 0 ? 0 : covered / total;

      bar.style.width = progress * 100 + "%";
    };

    splide.on("overflow", (isOverflow) => {
      splide.options = { drag: isOverflow };

      if (navigation) {
        navigation.style.display = isOverflow ? "flex" : "none";
      }
    });

    splide.on("mounted move", updateProgress);

    splide.mount();
    this.splides.set(tabId, splide);
  }
}

customElements.define("image-gallery", ImageGallery);

class SwymWishlist {
  static swymCallbackFn(swat) {
    document.addEventListener("swym:refresh-products", (event) => {
      const elementSelector = event.detail.selector;
      swat.initializeActionButtons(elementSelector);
    });
  }

  static initializeSwymCallbacks() {
    if (!window.SwymCallbacks) {
      window.SwymCallbacks = [];
    }

    window.SwymCallbacks.push(SwymWishlist.swymCallbackFn);
  }
}

SwymWishlist.initializeSwymCallbacks();


class TiktokCard extends HTMLElement {
  static activeInstance = null;

  constructor() {
    super();

    this.embed = this.querySelector(".js-card-embed");
    this.videoUrl = this.embed?.getAttribute("data-tiktok-url");
    this.loaded = false;
    this.isPlaying = false;
    this.iframe = null;
    this.posterRequested = false;
    this.btn = this.querySelector(".card-ugc__btn");

    if (!this.videoUrl) return;

    const width = window.innerWidth;
    this.isMobileMode = width <= 749;
    this.isTabletMode = width > 749 && width <= 990;
    this.isDesktopMode = width > 990;

    window.addEventListener("resize", () => {
      const newWidth = window.innerWidth;
      const wasMobile = this.isMobileMode;
      const wasTablet = this.isTabletMode;

      this.isMobileMode = newWidth <= 749;
      this.isTabletMode = newWidth > 749 && newWidth <= 990;
      this.isDesktopMode = newWidth > 990;

      if (
        (wasMobile || wasTablet) !== (this.isMobileMode || this.isTabletMode) &&
        this.isPlaying
      ) {
        this.pauseVideo();
      }
    });

    this.initPosterObserver();

    if (this.isMobileMode || this.isTabletMode) {
      this.addEventListener("click", (e) => {
        if (e.target.closest(".card-ugc__btn")) return;

        if (!this.loaded) {
          this.loadEmbed();
          this.loaded = true;
          setTimeout(() => this.playVideo(), 200);
        } else {
          if (this.isPlaying) {
            this.pauseVideo();
          } else {
            this.playVideo();
            this.iframe.style.pointerEvents = "auto";
          }
        }
      });

      this.btn?.addEventListener("click", (e) => {
        e.stopPropagation();
        this.toggleProductOverlay(e);
      });

      this.setupOutsideClickListener();

      if (this.isMobileMode) {
        this.initIntersectionObserver();
      }
    } else {
      this.addEventListener("mouseenter", () => {
        if (!this.loaded) {
          this.loadEmbed();
          this.loaded = true;
        }
        this.playVideo();
      });

      this.addEventListener("mouseleave", () => {
        this.pauseVideo();
      });
    }
  }

  initPosterObserver() {
    if (!("IntersectionObserver" in window)) {
      this.loadPoster();
      return;
    }

    this.posterObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.posterObserver.disconnect();
        this.loadPoster();
      },
      {
        rootMargin: "600px 0px",
        threshold: 0,
      },
    );

    this.posterObserver.observe(this);
  }

  initIntersectionObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!this.loaded) {
              this.loadEmbed();
              this.loaded = true;
              setTimeout(() => this.playVideo(), 200);
            } else {
              this.playVideo();
            }
          } else {
            this.pauseVideo();
          }
        });
      },
      { threshold: 0.6 },
    );

    this.observer.observe(this);
  }

  loadPoster() {
    if (this.posterRequested || this.loaded) return;
    this.posterRequested = true;

    fetch(
      `https://www.tiktok.com/oembed?url=${encodeURIComponent(this.videoUrl)}`,
    )
      .then((response) => {
        if (!response.ok) throw new Error(`TikTok oEmbed ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (!this.loaded && data.thumbnail_url) {
          this.embed.style.backgroundImage = `url(${data.thumbnail_url})`;
        }
        this.embed.classList.remove("loading");
      })
      .catch((error) => {
        console.error("Failed to load TikTok poster", error);
        this.embed.classList.remove("loading");
      });
  }

  loadEmbed() {
    this.posterObserver?.disconnect();

    const videoId = this.getVideoIdFromUrl(this.videoUrl);
    if (!videoId) return;

    this.iframe = document.createElement("iframe");
    this.iframe.src = `https://www.tiktok.com/player/v1/${videoId}?&fullscreen_button=0&loop=1&autoplay=1&rel=0&play_button=0&timestamp=0&progress_bar=0&closed_caption=0&wmode=opaque`;
    this.iframe.allow = "autoplay; fullscreen";
    this.iframe.setAttribute("wmode", "Opaque");
    this.iframe.setAttribute("frameborder", "0");
    this.iframe.setAttribute("scrolling", "no");
    this.iframe.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border: none;
      background: black;
    `;

    this.embed.innerHTML = "";
    this.embed.appendChild(this.iframe);
  }

  playVideo() {
    if (!this.iframe) return;

    if (
      this.isMobileMode &&
      TiktokCard.activeInstance &&
      TiktokCard.activeInstance !== this
    ) {
      TiktokCard.activeInstance.pauseVideo();
    }

    this.isPlaying = true;
    TiktokCard.activeInstance = this;
    this.sendMessage({ type: "play" });
  }

  pauseVideo() {
    if (!this.iframe) return;
    this.isPlaying = false;
    if (TiktokCard.activeInstance === this) {
      TiktokCard.activeInstance = null;
    }
    this.sendMessage({ type: "pause" });
  }

  sendMessage(message) {
    if (!this.iframe?.contentWindow) return;
    this.iframe.contentWindow.postMessage(
      { "x-tiktok-player": true, ...message },
      "https://www.tiktok.com",
    );
  }

  getVideoIdFromUrl(url) {
    const match = url.match(/\/video\/(\d+)/);
    return match ? match[1] : null;
  }

  setupOutsideClickListener() {
    const handleClickOutside = (e) => {
      if (!this.contains(e.target)) {
        const wrapper = this.querySelector(".card-ugc__btn-wrapper");
        if (wrapper && wrapper.classList.contains("active")) {
          wrapper.classList.remove("active");
        }
      }

      if (this.isTabletMode && !this.contains(e.target) && this.isPlaying) {
        this.pauseVideo();
        this.iframe.style.pointerEvents = "none";
      }
    };

    this.outsideClickListener = handleClickOutside;
    document.addEventListener("click", handleClickOutside, true);
  }

  toggleProductOverlay(e) {
    const wrapper = this.querySelector(".card-ugc__btn-wrapper");
    if (!wrapper) return;

    const isActive = wrapper.classList.contains("active");

    if (isActive) {
      wrapper.classList.remove("active");
    } else {
      wrapper.classList.add("active");

      const overlay = wrapper.nextElementSibling;
      if (overlay) {
        overlay.style.pointerEvents = "none";

        setTimeout(() => {
          if (overlay.style.pointerEvents === "none") {
            overlay.style.pointerEvents = "auto";
          }
        }, 300);
      }
    }
  }

  disconnectedCallback() {
    if (this.posterObserver) {
      this.posterObserver.disconnect();
    }
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.outsideClickListener) {
      document.removeEventListener("click", this.outsideClickListener, true);
    }
  }
}

customElements.define("tiktok-card", TiktokCard);

//mobile search toggle
document.addEventListener("DOMContentLoaded", () => {
  const searchToggles = document.querySelectorAll(".js-search-toggle");
  if (searchToggles.length) {
    searchToggles.forEach((toggle) => {
      ["touchstart", "mousedown"].forEach((eventName) => {
        toggle.addEventListener(eventName, (e) => {
          e.preventDefault();
          e.stopPropagation();

          if (
            document
              .querySelector(".header")
              .classList.contains("header--search-active")
          ) {
            document
              .querySelector(".header")
              .classList.remove("header--search-active");
            document.body.classList.remove("blur");
          } else {
            document
              .querySelector(".header")
              .classList.add("header--search-active");
            document.body.classList.add("blur");
            document.querySelector("#Search-In-Modal--mobile").focus();
          }
        });
      });
    });
  }
});

//Header scrollable class toggle
document.addEventListener("DOMContentLoaded", () => {
  const headerWrapper = document.querySelector(".section-header");

  if (!headerWrapper) return;

  if (window.scrollY > 0) {
    headerWrapper.classList.add("header--scrollable");
  }

  function onScroll() {
    if (window.scrollY > 0) {
      headerWrapper.classList.add("header--scrollable");
    } else {
      headerWrapper.classList.remove("header--scrollable");
    }
  }

  window.addEventListener("scroll", onScroll);
});

function initContentCardCarousel() {
  const splideElements = document.querySelectorAll(".js--splide-carousel");

  splideElements.forEach(function (splideEl) {
    if (splideEl.splideInstance) {
      splideEl.splideInstance.destroy();
    }

    const isMobileCarousel = splideEl.dataset.mobileCarousel === "true";

    let splideOptions = {
      perPage: 4,
      perMove: 1,
      arrows: true,
      pagination: false,
      drag: true,
      speed: 600,
      gap: 20,
      breakpoints: {
        1200: {
          perPage: 3,
          destroy: false,
        },
        749: {
          perPage: isMobileCarousel ? 1 : 3,
          destroy: !isMobileCarousel,
          noDrag: isMobileCarousel
            ? ".product-gallery-splide.splide--draggable, .product-gallery-splide.splide--draggable *"
            : "",
        },
      },
    };

    const splide = new Splide(splideEl, splideOptions);

    const navigation = splide.root.querySelector(".carousel-navigation");
    const bar = navigation.querySelector(".carousel-navigation-progress-bar");

    function updateProgress() {
      if (!bar) return;

      const total = splide.length;
      const perPage = splide.options.perPage;
      const currentIndex = splide.index;

      const covered = Math.min(currentIndex + perPage, total);

      const progress = total === 0 ? 0 : covered / total;

      bar.style.width = progress * 100 + "%";
    }

    splide.on("overflow", function (isOverflow) {
      splide.options = {
        drag: isOverflow,
      };

      if (navigation) {
        navigation.style.display = isOverflow ? "flex" : "none";
      }
    });

    splide.on("mounted move", updateProgress);

    splideEl.splideInstance = splide;
    splide.mount();
  });
}

document.addEventListener("DOMContentLoaded", initContentCardCarousel);
document.addEventListener("shopify:section:load", initContentCardCarousel);

// Wishlist Overrides
function installSwymAjaxCartOverride() {
  if (window.ttSwymAjaxCartOverrideInstalled) return true;
  if (typeof window._SwymAJAXCart !== "function") return false;

  const originalSwymAjaxCart = window._SwymAJAXCart;
  window.ttSwymAjaxCartOverrideInstalled = true;

  window._SwymAJAXCart = async function () {
    const result = await originalSwymAjaxCart.apply(this, arguments);

    document.querySelector(".swym-storefront-layout-backdrop")?.click();

    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: "swym",
      productVariantId: null,
    });

    document.body.dataset.cartAction = "added";

    document
      .getElementById("mini-cart")
      ?.dispatchEvent(new Event("drawer-toggle"));

    return result;
  };

  return true;
}

window.SwymCallbacks = window.SwymCallbacks || [];
window.SwymCallbacks.push(installSwymAjaxCartOverride);

// SwymCallbacks is the primary ready signal. A short, bounded fallback covers
// stores where the app defines its legacy global without invoking callbacks.
let swymOverrideAttempts = 0;
function initialiseSwymAjaxCartOverride() {
  if (installSwymAjaxCartOverride() || swymOverrideAttempts >= 20) return;

  swymOverrideAttempts += 1;
  setTimeout(initialiseSwymAjaxCartOverride, 500);
}

initialiseSwymAjaxCartOverride();
window.addEventListener("load", installSwymAjaxCartOverride, { once: true });

// Full Width Vimeo Iframes
document
  .querySelectorAll('iframe[src*="player.vimeo.com"]')
  .forEach((iframe) => {
    iframe.style.width = "100%";
    iframe.style.height = "auto";
    iframe.style.aspectRatio = "16 / 9";
    iframe.style.border = "0";
    iframe.style.display = "block";
  });
